import { useCallback, useEffect, useMemo, useState } from 'react'
import { Archive, ArchiveRestore, Ban, ClipboardEdit, PackageCheck, Paperclip } from 'lucide-react'
import { ContextMenu, type ContextMenuItem, type ContextMenuPosition } from '@/components/ui/ContextMenu'
import { Button } from '@/components/ui/Button'
import { shouldCloseDialog, useEscapeClose } from '@/hooks/useEscapeClose'
import { archivePurchaseOrder, listReceipts, patchReceipt, unarchivePurchaseOrder, voidReceipt, type PurchaseOrder, type ReceiptDto } from '@/services/procurement'
import {
  listFreeReceiptsByPo,
  patchFreeReceipt,
  voidFreeReceipt,
  type FreeReceiptDto,
} from '@/services/procurement/freePoApi'
import { PoQuickAttachmentDialog } from './PoQuickAttachmentDialog'
import { isPoArchiveEligible } from './poWorkflow'

type Receipt = ReceiptDto | FreeReceiptDto

export function canContinueReceiving(po: PurchaseOrder, canWrite: boolean): boolean {
  return canWrite && (po.status === 'SENT' || po.status === 'PARTIAL') && po.lines.some((line) => {
    return line.qty - line.receivedQty - line.shortQty > 0
  })
}

export function buildPoReceiveMenuItems({
  po,
  canWrite,
  canWriteAttachments = false,
  canWriteArchive = canWrite || canWriteAttachments,
  receiptCount,
  onContinue,
  onEdit,
  onVoid,
  onAttach,
  onArchive,
  onUnarchive,
}: {
  po: PurchaseOrder
  canWrite: boolean
  canWriteAttachments?: boolean
  canWriteArchive?: boolean
  receiptCount: number | null
  onContinue: () => void
  onEdit: () => void
  onVoid: () => void
  onAttach?: () => void
  onArchive?: () => void
  onUnarchive?: () => void
}): ContextMenuItem[] {
  const items: ContextMenuItem[] = []
  if (canContinueReceiving(po, canWrite)) {
    items.push({ id: 'continue-receiving', label: 'Tiếp tục nhận hàng', icon: <PackageCheck className="h-4 w-4" />, onClick: onContinue })
  }
  const canManageReceipts = canWrite && receiptCount !== 0
  if (canManageReceipts) {
    const suffix = receiptCount == null ? '' : ` (${receiptCount})`
    items.push({ id: 'edit-receipt', label: `Sửa phiếu${suffix}`, icon: <ClipboardEdit className="h-4 w-4" />, onClick: onEdit })
    items.push({ id: 'void-receipt', label: `Hủy phiếu${suffix}`, icon: <Ban className="h-4 w-4" />, variant: 'danger', onClick: onVoid })
  }
  if (canWriteAttachments && onAttach) {
    items.push({ id: 'add-attachment', label: 'Thêm tài liệu', icon: <Paperclip className="h-4 w-4" />, onClick: onAttach })
  }
  // Lưu trữ: chỉ PO đã đóng (đã nhận đủ, đã hủy, hoặc thiếu hàng đã xử lý); cho phép cả vai trò kho (receiving.write) và mua hàng (po.write).
  if (canWriteArchive) {
    if (po.archivedAt && onUnarchive) {
      items.push({ id: 'unarchive-po', label: 'Bỏ lưu trữ', icon: <ArchiveRestore className="h-4 w-4" />, onClick: onUnarchive })
    } else if (!po.archivedAt && onArchive && isPoArchiveEligible(po)) {
      items.push({ id: 'archive-po', label: 'Lưu trữ', icon: <Archive className="h-4 w-4" />, onClick: onArchive })
    }
  }
  return items
}

function receiptDate(receipt: Receipt): string {
  return 'received_at' in receipt ? receipt.received_at : receipt.created_at
}

function receiptUser(receipt: Receipt): string {
  return receipt.received_by
}

function receiptIsVoid(receipt: Receipt): boolean {
  return 'status' in receipt ? receipt.status === 'VOID' : Boolean(receipt.is_void)
}

function receiptLineId(line: { id: string }): string {
  return line.id
}

export function PoReceiptDialog({
  po,
  mode,
  onClose,
  onUpdated,
}: {
  po: PurchaseOrder
  mode: 'edit' | 'void'
  onClose: () => void
  onUpdated: () => void | Promise<void>
}) {
  const [receipts, setReceipts] = useState<Receipt[]>([])
  const [selectedId, setSelectedId] = useState('')
  const [quantities, setQuantities] = useState<Record<string, string>>({})
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    const load = po.kind === 'FREE' ? listFreeReceiptsByPo(po.id) : listReceipts({ po_id: po.id })
    void load.then((data) => {
      if (!active) return
      const valid = data.filter((receipt) => !receiptIsVoid(receipt)) as Receipt[]
      setReceipts(valid)
      setSelectedId(valid[0]?.id ?? '')
      setLoading(false)
    }).catch((err) => {
      if (!active) return
      setError(err instanceof Error ? err.message : 'Không tải được danh sách phiếu nhận')
      setLoading(false)
    })
    return () => { active = false }
  }, [po.id, po.kind])

  const selected = useMemo(() => receipts.find((receipt) => receipt.id === selectedId) ?? null, [receipts, selectedId])

  const hasUnsavedChanges = mode === 'void'
    ? Boolean(reason.trim())
    : Boolean(reason.trim()) || (selected?.lines ?? []).some((line) => {
      const original = 'qty' in line ? line.qty : line.qty_order_uom
      return (quantities[line.id] ?? String(original)) !== String(original)
    })

  const requestClose = useCallback(() => {
    if (saving || !shouldCloseDialog(hasUnsavedChanges)) return
    onClose()
  }, [hasUnsavedChanges, onClose, saving])

  useEscapeClose({ enabled: true, hasUnsavedChanges, onClose: requestClose })

  useEffect(() => {
    if (!selected || mode !== 'edit') return
    const next: Record<string, string> = {}
    for (const line of selected.lines ?? []) next[receiptLineId(line)] = String('qty' in line ? line.qty : line.qty_order_uom)
    setQuantities(next)
    setReason('')
  }, [selected, mode])

  async function save() {
    if (!selected) return
    if (!reason.trim()) {
      setError(mode === 'edit' ? 'Vui lòng nhập lý do sửa phiếu.' : 'Vui lòng nhập lý do hủy phiếu.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (mode === 'void') {
        if (po.kind === 'FREE') await voidFreeReceipt(selected.id, reason.trim())
        else await voidReceipt(selected.id, reason.trim())
      } else if (po.kind === 'FREE') {
        await patchFreeReceipt(selected.id, {
          reason: reason.trim(),
          lines: (selected.lines ?? []).map((line) => ({ id: line.id, qty: Number(quantities[line.id] ?? 0) })),
        })
      } else {
        await patchReceipt(selected.id, {
          lines: (selected.lines ?? []).map((line) => ({ id: line.id, qty: Number(quantities[line.id] ?? 0) })),
        }, reason.trim())
      }
      await onUpdated()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể lưu thay đổi phiếu nhận')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(event) => { if (event.currentTarget === event.target) requestClose() }}>
      <div className="w-full max-w-lg rounded-[var(--radius-md)] bg-white p-5 shadow-xl" role="dialog" aria-modal="true" aria-label={mode === 'edit' ? 'Sửa phiếu nhận' : 'Hủy phiếu nhận'}>
        <h3 className={`text-sm font-semibold ${mode === 'void' ? 'text-red-700' : 'text-slate-800'}`}>{mode === 'edit' ? 'Sửa phiếu nhận' : 'Hủy phiếu nhận'} — {po.code}</h3>
        {loading ? <div className="py-6 text-center text-xs text-slate-500">Đang tải phiếu nhận...</div> : receipts.length === 0 ? <div className="py-6 text-center text-xs text-slate-500">PO chưa có phiếu nhận hợp lệ.</div> : (
          <>
            <label className="mt-3 block text-xs font-semibold text-slate-700">Chọn phiếu</label>
            <select className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-xs" value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>
              {receipts.map((receipt) => <option key={receipt.id} value={receipt.id}>{receipt.code} · {receiptDate(receipt)} · {receiptUser(receipt)}</option>)}
            </select>
            {mode === 'edit' ? <div className="mt-3 max-h-56 space-y-2 overflow-y-auto">{(selected?.lines ?? []).map((line, index) => <div key={line.id} className="flex items-center justify-between gap-3 text-xs"><span>{index + 1}. {('name' in line && line.name) || `Dòng ${index + 1}`} ({('uom' in line && line.uom) || ('order_uom' in line && line.order_uom) || '—'})</span><input aria-label={`Số lượng dòng ${index + 1}`} className="h-7 w-24 rounded border border-slate-300 px-2 text-right font-mono" type="number" min="0" value={quantities[line.id] ?? ''} onChange={(event) => setQuantities((current) => ({ ...current, [line.id]: event.target.value }))} /></div>)}</div> : <p className="mt-3 text-xs text-red-700">Lý do là bắt buộc. Hủy phiếu sẽ hoàn tác số lượng đã nhập; hệ thống sẽ từ chối nếu làm tồn kho âm.</p>}
            <label className="mt-3 block text-xs font-semibold text-slate-700">Lý do <span className="text-red-500">*</span></label>
            <textarea className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-xs" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
            {error ? <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">{error}</div> : null}
            <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-3"><Button size="sm" variant="secondary" onClick={requestClose} disabled={saving}>Đóng</Button><Button size="sm" variant={mode === 'void' ? 'danger' : 'primary'} onClick={() => void save()} disabled={saving || !selected}>{saving ? 'Đang lưu...' : mode === 'edit' ? 'Xác nhận sửa' : 'Xác nhận hủy phiếu'}</Button></div>
          </>
        )}
      </div>
    </div>
  )
}

export function PoReceiveContextMenu({
  po,
  position,
  canWrite,
  canWriteAttachments,
  onClose,
  onContinue,
  onUpdated,
}: {
  po: PurchaseOrder | null
  position: ContextMenuPosition | null
  canWrite: boolean
  canWriteAttachments: boolean
  onClose: () => void
  onContinue: (po: PurchaseOrder) => void
  onUpdated: () => void | Promise<void>
}) {
  const [receiptCount, setReceiptCount] = useState<number | null>(null)
  const [dialog, setDialog] = useState<'edit' | 'void' | null>(null)
  const [dialogPo, setDialogPo] = useState<PurchaseOrder | null>(null)
  const [attachmentPo, setAttachmentPo] = useState<PurchaseOrder | null>(null)

  useEffect(() => {
    if (!po || !position) return
    let active = true
    const load = po.kind === 'FREE' ? listFreeReceiptsByPo(po.id) : listReceipts({ po_id: po.id })
    void load.then((data) => { if (active) setReceiptCount(data.filter((receipt) => !receiptIsVoid(receipt)).length) }).catch(() => { if (active) setReceiptCount(null) })
    return () => { active = false }
  }, [po, position])

  async function runArchive(target: PurchaseOrder, archive: boolean) {
    try {
      if (archive) await archivePurchaseOrder(target.id)
      else await unarchivePurchaseOrder(target.id)
      await onUpdated()
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Không thể cập nhật lưu trữ PO')
    }
  }

  const activeDialogPo = dialogPo ?? po
  if (!po || !position) {
    return <>
      {dialog && activeDialogPo ? <PoReceiptDialog po={activeDialogPo} mode={dialog} onClose={() => { setDialog(null); setDialogPo(null) }} onUpdated={onUpdated} /> : null}
      {attachmentPo ? <PoQuickAttachmentDialog po={attachmentPo} onClose={() => setAttachmentPo(null)} /> : null}
    </>
  }
  const items = buildPoReceiveMenuItems({
    po,
    canWrite,
    canWriteAttachments,
    receiptCount,
    onContinue: () => { onClose(); onContinue(po) },
    onEdit: () => { setDialogPo(po); setDialog('edit'); onClose() },
    onVoid: () => { setDialogPo(po); setDialog('void'); onClose() },
    onAttach: () => { setAttachmentPo(po); onClose() },
    onArchive: () => { onClose(); void runArchive(po, true) },
    onUnarchive: () => { onClose(); void runArchive(po, false) },
  })
  return <><ContextMenu isOpen position={position} onClose={onClose} items={items} />{dialog && activeDialogPo ? <PoReceiptDialog po={activeDialogPo} mode={dialog} onClose={() => { setDialog(null); setDialogPo(null) }} onUpdated={onUpdated} /> : null}</>
}
